import type { ComponentType } from "react";
import { ArrayField } from "@/shared/forms";
import { FieldsGridRow } from "./FieldsGridRow";
import { GridHeader } from "./GridHeader";
import type {
    BaseFieldSet,
    FieldDataToFieldPathFn,
    FieldMapperProps,
    FieldsGridConfig,
    FieldsGridGroup,
} from "./types";
import { useGridHeaderVisible } from "./useGridHeaderVisible";
import styles from "./FieldsGrid.module.scss";

interface FieldsGridProps<FieldGroup, FieldSet, AssertionsEnum extends string> {
    fieldGroup: FieldGroup;
    gridConfig: FieldsGridConfig<AssertionsEnum>;
    controlsRow: JSX.Element;
    FieldMapper: ComponentType<FieldMapperProps<AssertionsEnum>>;
    fieldSetToFormArrayPathFn: (fieldSet: FieldSet) => string;
    /** Identifies a row (React key, remove-button test id); cells bind via `fieldDataToFieldPathFn` instead. */
    fieldDataToFormFieldPathFn: (fieldset: FieldSet, index: number) => string;
    fieldDataToFieldPathFn: FieldDataToFieldPathFn<AssertionsEnum, FieldSet>;
    onRemoveItem: (params: { fieldName: string; index: number; fieldset: FieldSet }) => void;
}

export function FieldsGrid<
    AssertionsEnum extends string,
    FieldSet extends BaseFieldSet<AssertionsEnum>,
    FieldGroup extends FieldsGridGroup<AssertionsEnum, FieldSet>,
>(props: FieldsGridProps<FieldGroup, FieldSet, AssertionsEnum>) {
    const {
        fieldGroup,
        gridConfig,
        controlsRow,
        FieldMapper,
        fieldSetToFormArrayPathFn,
        fieldDataToFormFieldPathFn,
        fieldDataToFieldPathFn,
        onRemoveItem,
    } = props;
    const { Removable } = gridConfig;
    const { fieldsets, assertions } = fieldGroup;

    const isHeaderVisible = useGridHeaderVisible<AssertionsEnum, FieldSet>(fieldsets, fieldSetToFormArrayPathFn);

    return (
        <div className={styles.root}>
            {Removable && controlsRow}
            {isHeaderVisible && (
                <GridHeader<AssertionsEnum, FieldSet> gridConfig={gridConfig} assertions={assertions} />
            )}

            {fieldsets.map((fieldset) => {
                const arrayPath = fieldSetToFormArrayPathFn(fieldset);
                return (
                    <ArrayField key={arrayPath} name={arrayPath}>
                        {({ items }) =>
                            items?.map((_, index) => {
                                const rowPath = fieldDataToFormFieldPathFn(fieldset, index);
                                return (
                                    <FieldsGridRow<AssertionsEnum, FieldSet, FieldGroup>
                                        instanceIndex={index}
                                        key={rowPath}
                                        className={{ [styles.nonRemovableRow!]: !Removable }}
                                        gridConfig={gridConfig}
                                        fieldset={fieldset}
                                        fieldGroup={fieldGroup}
                                        onRemove={() => onRemoveItem({ fieldName: arrayPath, index, fieldset })}
                                        FieldMapper={FieldMapper}
                                        removeInstanceTestId={`removeInstance-${rowPath}`}
                                        fieldDataToFieldPathFn={fieldDataToFieldPathFn}
                                    />
                                );
                            })
                        }
                    </ArrayField>
                );
            })}
        </div>
    );
}
